import React from "react";
import { MountainSnow, Waves, Shield, Building2, User, Sparkles, Users } from "lucide-react";
import { team } from "../mock";
import Reveal from "./Reveal";

const cmap = { blue: "#187EE4", orange: "#FF6C10", red: "#FF1228", yellow: "#F1C91F" };

const BridgeIcon = ({ className = "", strokeWidth = 2.4 }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M2 17h20" />
    <path d="M4 17V9" />
    <path d="M20 17V9" />
    <path d="M4 10c4.5 4.2 11.5 4.2 16 0" />
    <path d="M8 17v-4.2" />
    <path d="M12 17v-4.8" />
    <path d="M16 17v-4.2" />
  </svg>
);

const iconMap = { mountain: MountainSnow, ocean: Waves, canyon: BridgeIcon };

const Connector = () => (
  <div className="flex justify-center" aria-hidden="true">
    <span className="block w-0.5 h-12 md:h-16 rounded-full" style={{ background: "linear-gradient(180deg, rgba(24,126,228,0.05), rgba(24,126,228,0.45))" }} />
  </div>
);

const ChapterHeader = ({ num, color, Icon, eyebrow, title }) => (
  <Reveal>
    <div className="flex flex-col items-center text-center mb-8">
      <span
        className="relative flex items-center justify-center w-14 h-14 rounded-full font-display text-xl text-white shadow-lg"
        style={{ backgroundColor: color, boxShadow: `0 10px 22px -8px ${color}99` }}
      >
        {num}
        <span className="absolute -right-1 -bottom-1 flex items-center justify-center w-7 h-7 rounded-full bg-white shadow-md">
          <Icon className="w-4 h-4" style={{ color }} />
        </span>
      </span>
      {eyebrow ? <p className="mt-4 font-display tracking-[0.2em] uppercase text-xs" style={{ color }}>{eyebrow}</p> : null}
      <h3 className="mt-1 font-display text-3xl md:text-5xl" style={{ color: "#187EE4" }}>{title}</h3>
    </div>
  </Reveal>
);

const TeamBraveMe = () => {
  const { riley, hq, crew } = team;
  return (
    <section
      id="about"
      className="py-16 md:py-24"
      style={{ background: "linear-gradient(180deg, rgba(24,126,228,0.07) 0%, rgba(24,126,228,0.03) 50%, rgba(255,108,16,0.05) 100%)" }}
    >
      <div className="max-w-6xl mx-auto px-5 md:px-8">
        <Reveal>
          <div className="text-center mb-14">
            <h2 className="font-display text-4xl md:text-6xl" style={{ color: "#187EE4" }}>Team Brave Me</h2>
            <p className="mt-4 text-slate-600 font-medium max-w-3xl mx-auto">From Riley&rsquo;s house, to Brave Me Headquarters &ndash; all of us wake up to bring courage to more kids and patients of all ages anywhere. Whether animated, as a puppet or a member of humankind, our team is dedicated to providing knowledge and respect to kids dealing with very hard things. Our goal is always to make life easier for all facing frightening medical care, moving fearful patients to brave and strong heroes!</p>
          </div>
        </Reveal>

        {/* Chapter 01 — Riley */}
        <ChapterHeader num="01" color={cmap.orange} Icon={Sparkles} eyebrow="" title="Meet Riley" />
        <Reveal>
          <div className="grid md:grid-cols-2 gap-8 items-center mb-10 rounded-[28px] bg-white/70 backdrop-blur-sm p-5 md:p-7 shadow-lg border border-white">
            <div className="relative rounded-[22px] overflow-hidden shadow-md">
              <img src={riley.image} alt={riley.name} className="w-full h-full object-cover" />
            </div>
            <div>
              <h4 className="font-display text-4xl md:text-5xl" style={{ color: "#FF6C10" }}>{riley.name}</h4>
              <p className="mt-4 text-slate-600 font-medium leading-relaxed">{riley.bio}</p>
            </div>
          </div>
        </Reveal>

        {/* Journey lands */}
        <div className="grid md:grid-cols-3 gap-6 mb-10">
          {riley.lands.map((land, i) => {
            const color = cmap[land.color];
            const Icon = iconMap[land.id] || Shield;
            return (
              <Reveal key={land.id} delay={i * 110}>
                <div className="group relative h-full rounded-[28px] overflow-hidden shadow-lg min-h-[340px] flex flex-col justify-end">
                  {land.image && <img src={land.image} alt={land.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />}
                  <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, ${color}40 0%, rgba(15,37,64,0.35) 40%, rgba(15,37,64,0.92) 100%)` }} />
                  <span className="absolute top-5 left-5 z-10 inline-flex items-center justify-center w-16 h-16 rounded-2xl shadow-xl ring-4 ring-white/30" style={{ backgroundColor: color }}><Icon className="w-8 h-8 text-white" strokeWidth={2.4} /></span>
                  <div className="relative p-6 text-white">
                    <h5 className="font-display text-2xl brave-shadow-text">{land.name}</h5>
                    <p className="mt-1.5 text-white/90 font-medium text-sm leading-relaxed">{land.blurb}</p>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>

        {/* Riley with doctor and nurse */}
        <Reveal>
          <div className="rounded-[28px] overflow-hidden shadow-lg border-4 border-white">
            <img src={riley.doctorImage} alt="Riley with the doctor and nurse" className="w-full h-auto block" />
          </div>
        </Reveal>

        <Connector />

        {/* Chapter 02 — Headquarters */}
        <ChapterHeader num="02" color={cmap.blue} Icon={Building2} eyebrow="Where the magic happens" title={hq.title} />
        <Reveal>
          <div className="rounded-[28px] overflow-hidden shadow-xl border-4 border-white mb-6">
            <img src={hq.image} alt={hq.title} className="w-full h-auto block" />
          </div>
          <p className="text-center text-slate-600 font-medium max-w-2xl mx-auto mb-10">{hq.blurb}</p>
        </Reveal>
        <div className="grid sm:grid-cols-2 gap-6 mb-4">
          {hq.characters.map((c, i) => {
            const color = i === 0 ? cmap.blue : cmap.red;
            return (
              <Reveal key={c.name} delay={i * 110}>
                <div className="h-full flex gap-5 rounded-3xl p-6 bg-white shadow-md hover:shadow-xl hover:-translate-y-1 transition-all duration-300 border border-slate-100 border-t-4" style={{ borderTopColor: color }}>
                  <div className="shrink-0 w-24 h-24 rounded-2xl overflow-hidden" style={{ boxShadow: `0 0 0 3px ${color}33` }}>
                    <img src={c.image} alt={c.name} className="w-full h-full object-cover object-center" />
                  </div>
                  <div>
                    <h4 className="font-display text-2xl" style={{ color }}>{c.name}</h4>
                    {c.role ? <p className="text-xs font-bold uppercase tracking-wide mb-2" style={{ color: "#FF6C10" }}>{c.role}</p> : <div className="mb-2" />}
                    <p className="text-slate-600 leading-relaxed font-medium text-sm">{c.bio}</p>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Connector />

        {/* Chapter 03 — The Crew */}
        <ChapterHeader num="03" color={cmap.red} Icon={Users} eyebrow="The people behind the courage" title="Brave Me Crew" />
        <div className="grid grid-cols-2 md:grid-cols-5 gap-5">
          {crew.map((m, i) => {
            const color = cmap[["orange", "blue", "red", "yellow", "blue"][i % 5]];
            return (
              <Reveal key={m.name} delay={i * 80}>
                <div className="text-center rounded-3xl p-5 bg-white shadow-md hover:shadow-xl hover:-translate-y-1 transition-all duration-300 border border-slate-100 border-t-4 h-full" style={{ borderTopColor: color }}>
                  <div className="mx-auto mb-3 w-20 h-20 rounded-full overflow-hidden flex items-center justify-center" style={{ backgroundColor: `${color}1a`, boxShadow: `0 0 0 3px ${color}33` }}>
                    {m.image ? <img src={m.image} alt={m.name} className="w-full h-full object-cover" /> : <User className="w-8 h-8" style={{ color }} />}
                  </div>
                  <p className="font-display text-lg leading-tight" style={{ color }}>{m.name}</p>
                  <p className="mt-1 text-xs font-semibold text-slate-500 leading-snug">{m.role}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default TeamBraveMe;
